import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Globe, ArrowRight, CheckCircle, Clock, Copy, AlertTriangle } from 'lucide-react';
import { useSEO } from '@/hooks/useSEO';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

export default function VerifyPage() {
  useSEO({
    title: 'Verify Domain | Acodia — Grow. Defend. Be Found.',
    description: 'Verify ownership of your domain before Acodia Watch monitoring begins.',
    path: '/verify',
    robots: 'noindex,nofollow',
  });
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [verifyMethod, setVerifyMethod] = useState('dns_txt');
  const [instructions, setInstructions] = useState(null);
  const [selectedDomainId, setSelectedDomainId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    try {
      const res = await authFetch(`${BACKEND_URL}/api/verify/domains`);
      if (res.ok) {
        const data = await res.json();
        setDomains(data.domains || []);
        
        // Check if any domain is verified - if so, redirect to dashboard
        const hasVerified = data.domains.some(d => d.verified);
        if (hasVerified) {
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (err) {
      console.error('Failed to load domains:', err);
    }
  };

  const startVerification = async () => {
    if (!newDomain) return;
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const res = await authFetch(`${BACKEND_URL}/api/verify/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: newDomain }),
      });
      const data = await res.json();
      
      if (res.ok) {
        setInstructions(data);
        setSelectedDomainId(data.domain_id);
        setNewDomain('');
        loadDomains();
      } else {
        setError(data.detail || 'Failed to start verification');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const runVerification = async () => {
    if (!selectedDomainId) return;
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await authFetch(`${BACKEND_URL}/api/verify/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain_id: selectedDomainId, method: verifyMethod }),
      });
      const data = await res.json();
      
      if (data.verified) {
        setSuccess('Domain verified successfully! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2000);
      } else {
        setError(data.message || 'Verification failed. Please check your DNS/HTML configuration and try again.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard');
    setTimeout(() => setSuccess(''), 2000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ background: 'var(--bg)' }}>
      <div className="max-w-2xl w-full">
        {/* Logo */}
        <a href="/" className="block text-center mb-8">
          <span className="font-display text-2xl font-bold" style={{ color: 'var(--white)' }}>acodia</span>
        </a>

        {/* Verification Required Card */}
        <div 
          className="p-8 rounded-lg mb-6"
          style={{ 
            background: 'var(--l1)', 
            border: '1px solid var(--b1)',
          }}
        >
          <div className="mb-6">
            <Globe size={48} style={{ color: 'var(--a)', margin: '0 auto 16px', display: 'block' }} />
            <h1 className="font-display text-2xl font-bold text-center mb-2" style={{ color: 'var(--white)' }}>
              Domain Verification Required
            </h1>
            <p className="font-body text-sm text-center" style={{ color: 'var(--tm)' }}>
              Verify at least one domain to access the Client Pulse Dashboard.
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded mb-4" style={{ background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
              <AlertTriangle size={14} style={{ color: 'var(--danger)' }} />
              <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{error}</p>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 p-3 rounded mb-4" style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)' }}>
              <CheckCircle size={14} style={{ color: 'var(--ok)' }} />
              <p className="font-body text-xs" style={{ color: 'var(--ok)' }}>{success}</p>
            </div>
          )}

          {/* Add Domain */}
          <div className="mb-6">
            <label className="font-body text-xs mb-2 block" style={{ color: 'var(--ts)' }}>Add Domain to Verify</label>
            <div className="flex gap-3">
              <Input
                data-testid="domain-input"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                placeholder="example.com"
                className="font-mono text-sm h-11 rounded-[3px] flex-1"
                style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid var(--b1)', color: 'var(--white)' }}
              />
              <Button
                data-testid="add-domain-button"
                onClick={startVerification}
                disabled={!newDomain || loading}
                className="font-body text-sm px-4 h-11 rounded-[3px]"
                style={{ background: 'var(--a)', color: '#fff' }}
              >
                Add Domain
              </Button>
            </div>
          </div>

          {/* Verification Instructions */}
          {instructions && (
            <div className="p-5 rounded-lg mb-6" style={{ background: 'var(--bg)', border: '1px solid rgba(139,92,246,0.25)' }}>
              <h3 className="font-display text-base font-bold mb-3" style={{ color: 'var(--white)' }}>
                Verify {instructions.domain}
              </h3>

              <p className="font-body text-xs mb-4" style={{ color: 'var(--tm)' }}>
                Choose a verification method and follow the instructions below:
              </p>

              {/* Method Selector */}
              <div className="flex gap-2 mb-4">
                {[
                  { id: 'dns_txt', label: 'DNS TXT' },
                  { id: 'html_file', label: 'HTML File' },
                  { id: 'meta_tag', label: 'Meta Tag' },
                ].map(m => (
                  <button
                    key={m.id}
                    onClick={() => setVerifyMethod(m.id)}
                    className="font-mono text-xs px-3 py-1.5 rounded-[3px]"
                    style={{
                      background: verifyMethod === m.id ? 'var(--a-glow)' : 'transparent',
                      border: `1px solid ${verifyMethod === m.id ? 'var(--a)' : 'var(--b1)'}`,
                      color: verifyMethod === m.id ? 'var(--a)' : 'var(--ts)',
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {/* Instructions Display */}
              {verifyMethod === 'dns_txt' && instructions.instructions?.dns_txt && (
                <div>
                  <p className="font-body text-xs mb-2" style={{ color: 'var(--ts)' }}>
                    Add this TXT record to your domain's DNS:
                  </p>
                  <div className="p-3 rounded font-mono text-xs mb-2 flex items-center justify-between" style={{ background: 'var(--l1)', border: '1px solid var(--b1)', color: 'var(--ok)' }}>
                    <span>{instructions.instructions.dns_txt.record}</span>
                    <button onClick={() => copyToClipboard(instructions.instructions.dns_txt.record)}>
                      <Copy size={14} style={{ color: 'var(--ts)' }} />
                    </button>
                  </div>
                </div>
              )}

              {verifyMethod === 'html_file' && instructions.instructions?.html_file && (
                <div>
                  <p className="font-body text-xs mb-2" style={{ color: 'var(--ts)' }}>
                    {instructions.instructions.html_file.description}
                  </p>
                  <div className="p-3 rounded font-mono text-xs mb-2" style={{ background: 'var(--l1)', border: '1px solid var(--b1)', color: 'var(--ok)' }}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs" style={{ color: 'var(--ts)' }}>File content:</span>
                      <button onClick={() => copyToClipboard(instructions.instructions.html_file.content)}>
                        <Copy size={14} style={{ color: 'var(--ts)' }} />
                      </button>
                    </div>
                    <span>{instructions.instructions.html_file.content}</span>
                  </div>
                </div>
              )}

              {verifyMethod === 'meta_tag' && instructions.instructions?.meta_tag && (
                <div>
                  <p className="font-body text-xs mb-2" style={{ color: 'var(--ts)' }}>
                    {instructions.instructions.meta_tag.description}
                  </p>
                  <div className="p-3 rounded font-mono text-xs mb-2 flex items-center justify-between" style={{ background: 'var(--l1)', border: '1px solid var(--b1)', color: 'var(--ok)' }}>
                    <span className="break-all">{instructions.instructions.meta_tag.tag}</span>
                    <button onClick={() => copyToClipboard(instructions.instructions.meta_tag.tag)} className="ml-2">
                      <Copy size={14} style={{ color: 'var(--ts)' }} />
                    </button>
                  </div>
                </div>
              )}

              <Button
                data-testid="verify-button"
                onClick={runVerification}
                disabled={loading}
                className="w-full font-body text-sm px-4 py-2 rounded-[3px] mt-4"
                style={{ background: 'var(--a)', color: '#fff', height: 'auto' }}
              >
                {loading ? 'Verifying...' : 'Run Verification'}
                {!loading && <ArrowRight size={14} className="ml-2" />}
              </Button>
            </div>
          )}

          {/* Existing Domains */}
          {domains.length > 0 && (
            <div>
              <h3 className="font-body text-sm font-medium mb-3" style={{ color: 'var(--white)' }}>
                Your Domains
              </h3>
              <div className="space-y-2">
                {domains.map(d => (
                  <div 
                    key={d.id} 
                    className="flex items-center justify-between p-3 rounded"
                    style={{ background: 'var(--bg)', border: '1px solid var(--b1)' }}
                  >
                    <div className="flex items-center gap-3">
                      <Globe size={16} style={{ color: d.verified ? 'var(--ok)' : 'var(--warn)' }} />
                      <div>
                        <p className="font-mono text-sm" style={{ color: 'var(--white)' }}>{d.domain}</p>
                        <p className="font-body text-xs" style={{ color: 'var(--ts)' }}>
                          {d.verified ? 'Verified' : 'Pending verification'}
                        </p>
                      </div>
                    </div>
                    {d.verified ? (
                      <CheckCircle size={16} style={{ color: 'var(--ok)' }} />
                    ) : (
                      <Clock size={16} style={{ color: 'var(--warn)' }} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="text-center">
          <a href="/dashboard" className="font-body text-xs underline" style={{ color: 'var(--ts)' }}>
            Back to Dashboard
          </a>
        </div>
      </div>
    </div>
  );
}
